import type { Locale } from '../constants'
import { isElderAge, isSeniorAge, isYouthAge } from '../viewer'

/**
 * 나이대에 맞는 주제.
 *
 * 만 2~11세 대운 풀이에 "일터나 사회 활동" 이야기가 나온다는 제보를 받았다.
 * 말투 규칙으로 부탁하는 것만으로는 막히지 않아서, **쓸 수 있는 주제를 코드에서 정해**
 * 프롬프트에 넣고, 나온 글도 검사한다.
 */

interface Band {
  from: number
  to: number
  ko: string
  en: string
}

const BANDS: Band[] = [
  { from: 0, to: 12, ko: '가정 분위기, 타고난 성향, 몸과 건강, 학교 적응, 또래 관계', en: 'the home atmosphere, inborn disposition, body and health, settling into school, friends of the same age' },
  { from: 13, to: 19, ko: '공부, 친구, 진로 고민, 부모와의 관계, 자기 정체성', en: 'studying, friends, wondering what to do next, the relationship with parents, working out who they are' },
  { from: 20, to: 29, ko: '학업 마무리, 첫 사회생활, 연애, 독립', en: 'finishing studies, the first working years, romance, living on their own' },
  // "결혼과 가정"·"자녀" → "가족" — 미혼에게 아이를 전제했다 (zh-Hans·es d4)
  { from: 30, to: 39, ko: '일, 가족과 가까운 관계, 돈 모으기, 방향 전환', en: 'work, family and close relationships, saving money, changing direction' },
  { from: 40, to: 49, ko: '일의 성숙, 가족, 건강, 인간관계 정리', en: 'work coming into its own, family, health, sorting out who to keep close' },
  { from: 50, to: 64, ko: '오래 해 온 일, 건강, 가족, 취미', en: 'long-held skills, health, family and hobbies' },
  { from: 65, to: 200, ko: '건강, 가족, 취미, 쉬는 시간, 가까운 사람', en: 'health, family, hobbies, rest and close relationships' },
]

/** 만 나이 한 살에 맞는 주제 묶음 */
function bandsFor(from: number, to: number): Band[] {
  return BANDS.filter((b) => b.from <= to && b.to >= from)
}

/**
 * 10년 구간에 쓸 수 있는 주제.
 * 한 구간이 두 나이대에 걸치면 양쪽 주제를 모두 허용한다 (걸친 나이를 자를 수 없다).
 */
export function ageTopics(startAge: number, locale: Locale): string {
  const bands = bandsFor(startAge, startAge + 9)
  const ko = locale === 'ko'
  return bands.map((b) => (ko ? b.ko : b.en)).join(ko ? ' / ' : ' / ')
}

/** 지금 나이대에 무엇이 궁금한가 (대운 말고 다른 섹션에 쓴다) */
export function ageInterest(age: number, locale: Locale): string {
  const ko = locale === 'ko'
  // 나이 갈래 경계는 viewer.ts (30살은 AI 재료 글만 가르는 자리라 그대로)
  if (isYouthAge(age)) return ko ? '공부·친구·진로처럼 아직 자리를 잡아 가는 이야기' : 'studying, friends and figuring out a direction — life is still taking shape'
  // 낱말 목록이 아니라 장면 — 30~40대는 경력이 있는 사람의 일 장면 (44살 직업 글이 초급 업무를 권했다)
  if (age < 30) return ko ? '첫 일자리나 진로를 고르는 장면, 새 사람을 만나는 장면' : 'choosing a first job or a direction, meeting someone new'
  if (!isSeniorAge(age)) return ko ? '맡은 일을 이끌고 후배를 챙기는 일터 장면, 가까운 사람과 주말 계획을 세우는 장면' : 'leading their part of the work and looking out for newer colleagues, planning a weekend with people close to them'
  if (!isElderAge(age)) return ko ? '오래 쌓은 경력으로 일을 넘겨주거나 조율하는 장면, 가까운 사람과 보내는 주말' : 'handing over or steering work with long experience, weekends with people close to them'
  return ko ? '가족과 보내는 시간, 취미와 산책, 쉬는 시간' : 'time with family, hobbies and walks, time to rest'
}

/**
 * 어린 나이 구간에 나오면 안 되는 말.
 * 사용자가 실제로 본 문제("만 2~11세 구간에 일터·사회 활동") 를 막는다.
 * 지시받은 아홉 낱말(직장·회사·사업·투자·재테크·승진·이직·매출·창업)만 언어별로 옮겼다 —
 * "직업" 처럼 넓은 말까지 넣으면 "부모가 하는 일" 같은 정상적인 문장이 걸린다.
 */
const YOUTH_WORK: Record<Locale, RegExp> = {
  ko: /직장|회사|사업|투자|재테크|승진|이직|매출|창업/,
  en: /\b(workplace|company|companies|business|invest|investing|investment|promotion|promoted|job change|changing jobs|revenue|sales figures|start-?up|found(?:ing)? a company)\b/i,
  ja: /職場|会社|事業|投資|資産運用|昇進|転職|売上|起業/,
  'zh-Hans': /职场|公司|事业|投资|理财|升职|跳槽|营业额|创业/,
  'zh-Hant': /職場|公司|事業|投資|理財|升職|跳槽|營業額|創業/,
  es: /\b(lugar de trabajo|empresa|negocio|inversi[oó]n|invertir|ascenso|cambio de trabajo|facturaci[oó]n|emprender)\b/i,
  'pt-BR': /\b(local de trabalho|empresa|neg[oó]cio|investimento|investir|promo[cç][aã]o|mudan[cç]a de emprego|faturamento|empreender)\b/i,
}

/** 이 10년 구간이 통째로 열아홉 살 아래인가 (스무 살에 걸치면 일 이야기를 해도 된다) */
export function isYouthSpan(startAge: number): boolean {
  return startAge + 9 <= 19
}

/** 어린 구간 글에 일·사업 말이 들어갔는가 */
export function youthWorkHits(text: string, locale: Locale): string[] {
  const m = YOUTH_WORK[locale].exec(text)
  return m ? [m[0]] : []
}

/*
 * ── : 사람 상황에 맞게 (걸리면 그 장을 통째로 다시 쓴다) ──
 */

/**
 * **20살 아래 — 모든 장** (`youthWorkHits` 를 넓힌 것). 어린 대운 칸의 검사(`youthWorkHits`)는 회사·사업이라는 낱말 자체를 막지만,
 * 20살 아래 사람의 일·사업 장은 "나중에 내 일을 해 본다면" 처럼 앞으로의 가능성을 쓰는 자리라 그 낱말은 둔다.
 * 여기서는 **지금 하고 있는 것처럼** 쓴 말만 본다 — 승진·인사평가·이직·매출·운영 중인 사업·직원·부부 생활.
 */
const YOUTH_NOW: Record<Locale, RegExp> = {
  ko: /결혼|직장|사업|회의|상사|카드값|고정 ?지출|경조사비|자동 ?결제|승진|인사 ?평가|업무 ?평가|이직|매출|연봉|회사|운영 중|직원|부부 ?생활|부부 ?사이|결혼 ?생활/,
  // "good job"(칭찬)과 "the company of friends"(친구들과 함께)은 일터가 아니다.
  // 현재 일자리·채용 맥락을 명시한 경우에만 job 을 잡는다.
  en: /\bmarri(?:age|ed)|\bwedding|\b(?:current|full-time|part-time|new)\s+job\b|\b(?:job\s+(?:change|interview|application|offer|search|hunt))\b|\b(?:quit|leave|left|apply\s+for)\s+(?:a\s+|the\s+|your\s+)?job\b|\bworkplace|\bbusiness|\bmeeting\b(?!\s+(?:(?:a|your|new|the|my|other)\s+)*(?:classmates?|friends?|teachers?|students?|kids?|neighbors?|family|people)\b)|\bboss|\bcredit card bill|\bfixed expense|\bautomatic payment|\bautopay|\bfamily event expense|\bpromotion|\bperformance review|\brevenue|\bsalary|\bcompany\b(?!\s+of\b)/i,
  ja: /結婚|職場|事業|会議|上司|カード代|固定費|自動決済|冠婚葬祭費|昇進|人事評価|査定|転職|売上|年収|会社|従業員|夫婦/,
  'zh-Hans': /结婚|职场|事业|会议|上司|信用卡账单|固定支出|自动扣款|人情支出|升职|绩效|考核|跳槽|营业额|年薪|公司|员工|夫妻/,
  'zh-Hant': /結婚|職場|事業|會議|上司|信用卡帳單|固定支出|自動扣款|人情支出|升職|績效|考核|跳槽|營業額|年薪|公司|員工|夫妻/,
  es: /\bmatrimonio|\bboda|\bempresa|\bnegocio|\breuni[oó]n de trabajo|\bjefe|\btarjeta de cr[eé]dito|\bgastos fijos|\bpago autom[aá]tico|\bgastos de celebraci[oó]n|\bascenso|\bfacturaci[oó]n|\bsueldo|\bsalario\b/i,
  'pt-BR': /\bcasamento|\bempresa|\bneg[oó]cio|\breuni[aã]o de trabalho|\bchefe|\bcart[aã]o de cr[eé]dito|\bdespesas fixas|\bpagamento autom[aá]tico|\bdespesas de cerim[oô]nia|\bpromo[cç][aã]o|\bfaturamento|\bsal[aá]rio\b/i,
}
export function youthNowHits(text: string, locale: Locale): string[] {
  const m = YOUTH_NOW[locale].exec(text)
  return m ? [m[0].trim()] : []
}

const SENIOR_WORK: Record<Locale, RegExp> = {
  ko: /이직|면접|회의|상사|일이 아무리 바빠도|일한 대가|승진|인사 ?평가|퇴직|사회생활에 큰 변화|직장 생활에 큰 변화/,
  en: /\bjob change|\bjob interview|\bwork meeting|\bboss|\bno matter how busy work|\bpayment for work|\bpromotion|\bretire(?:ment|d)?\b/i,
  ja: /転職|就職面接|仕事の会議|上司|仕事が忙しくても|仕事の報酬|昇進|退職/,
  'zh-Hans': /跳槽|求职面试|工作会议|上司|工作再忙|工作报酬|升职|退休/,
  'zh-Hant': /跳槽|求職面試|工作會議|上司|工作再忙|工作報酬|升職|退休/,
  es: /\bcambio de trabajo|\bentrevista de trabajo|\breuni[oó]n de trabajo|\bjefe|\bpago por el trabajo|\bascenso|\bjubilaci[oó]n\b/i,
  'pt-BR': /\bmudan[cç]a de emprego|\bentrevista de emprego|\breuni[aã]o de trabalho|\bchefe|\bpagamento pelo trabalho|\bpromo[cç][aã]o|\baposentadoria\b/i,
}
export function seniorWorkHits(text: string, locale: Locale): string[] {
  const match = SENIOR_WORK[locale].exec(text)
  return match ? [match[0].trim()] : []
}

/**
 * **이미 함께 사는 말** — 싱글·모름·연애 중·20살 아래의 결혼 장 (`marriedGuardHits` 와 반대 방향). 결혼 장 뜻이
 * "함께 사는 생활·부부 사이" 라 61세 싱글에게 살림 돈·부부 사이를 지금 일처럼 썼다 (es "dinero de la casa", zh "一起过日子").
 */
const LIVING_TOGETHER: Record<Locale, RegExp> = {
  ko: /살림|생활비|부부 ?사이|부부 ?생활|부부간|결혼 ?생활|남편|아내/,
  en: /\bhousehold (?:money|budget|bills|expenses|finances)\b|\bmarried life\b|\bas a married couple\b|\bbetween spouses\b|\byour (?:spouse|husband|wife)\b/i,
  ja: /夫婦|結婚生活|家計|生活費|配偶者|ご主人|奥さん|旦那/,
  'zh-Hans': /一起过日子|家里的开销|家庭开支|家用|夫妻|婚姻生活|另一半|配偶|老公|老婆|丈夫|妻子/,
  'zh-Hant': /一起過日子|家裡的開銷|家庭開支|家用|夫妻|婚姻生活|另一半|配偶|老公|老婆|丈夫|妻子/,
  es: /\bdinero de la casa\b|\bgastos de la casa\b|\bcuentas de la casa\b|\bvida (?:de casad[oa]s?|matrimonial)\b|\btu (?:c[oó]nyuge|esposo|esposa|marido)\b/i,
  'pt-BR': /\bcontas da casa\b|\bdinheiro da casa\b|\bgastos da casa\b|\bvoc[eê]s j[aá] constru[ií]ram\b|\bvida (?:de casad[oa]s?|conjugal)\b|\bseu (?:c[oô]njuge|marido)\b|\bsua esposa\b/i,
}
export function livingTogetherHits(text: string, locale: Locale): string[] {
  const m = LIVING_TOGETHER[locale].exec(text)
  return m ? [m[0].trim()] : []
}

/**
 * **지난 10년 칸의 권하는 말** — 지난 대운에 "~해 봐 / 试试" 처럼 지금 할 일을 권했다 (zh-Hans 36세 d1~d3).
 * 지난 칸은 돌아보는 말로만 쓴다.
 */
const PAST_ADVICE: Record<Locale, RegExp> = {
  ko: /[가-힣] ?보세요|[가-힣] ?봐요|해 ?봐(?![가-힣])|하세요|하시길|하면 좋아요|하는 게 좋아요|두세요|챙기세요/,
  en: /\btry\b|\bconsider\b|\bmake sure\b|\byou should\b|\bit helps to\b|\bset aside\b/i,
  ja: /てみて|でみて|しましょう|みましょう|てください|でください|するといい|すると良い|するとよい/,
  'zh-Hans': /试试|试着|不妨|可以试|建议你|建议/,
  'zh-Hant': /試試|試著|不妨|可以試|建議你|建議/,
  es: /\bintenta\b|\bprueba a\b|\bprocura\b|\bte conviene\b|\bdeber[ií]as\b|\bconsidera\b/i,
  'pt-BR': /\btente\b|\bexperimente\b|\bprocure\b|\bvale a pena\b|\bconv[eé]m\b|\bdeveria\b|\bconsidere\b/i,
}
export function pastAdviceHits(text: string, locale: Locale): string[] {
  const m = PAST_ADVICE[locale].exec(text)
  return m ? [m[0].trim()] : []
}

/**
 * **지어낸 나이 구간** — 10년 흐름의 나이와 맞지 않는 "45~49세" (es 인생 그래프가 지금 10년 39~48세를 "entre los 45 y los 49 años" 로 썼다).
 * 구간의 시작이 어느 10년의 시작 나이이고 끝이 어느 10년의 끝(시작 + 9 또는 + 10)일 때만 맞는 구간이다.
 */
const AGE_RANGE: Record<Locale, RegExp> = {
  ko: /(\d{1,3})\s*(?:세|살)?\s*(?:~|-|–|부터)\s*(?:만\s*)?(\d{1,3})\s*(?:세|살)/g,
  en: /\b(?:ages?|between|from)\s+(\d{1,3})\s*(?:-|–|to|and)\s*(\d{1,3})\b/gi,
  ja: /(\d{1,3})\s*歳\s*(?:から|～|~|〜|-)\s*(\d{1,3})\s*歳/g,
  'zh-Hans': /(\d{1,3})\s*岁?\s*(?:到|至|~|～|-)\s*(\d{1,3})\s*岁/g,
  'zh-Hant': /(\d{1,3})\s*歲?\s*(?:到|至|~|～|-)\s*(\d{1,3})\s*歲/g,
  es: /\b(?:entre|de)\s+los\s+(\d{1,3})\s+(?:y|a)\s+los\s+(\d{1,3})\s+años/gi,
  'pt-BR': /\b(?:entre|dos)\s+(?:os\s+)?(\d{1,3})\s+(?:e|a|aos)\s+(?:os\s+)?(\d{1,3})\s+anos/gi,
}
export function strayAgeRanges(text: string, locale: Locale, decadeStarts: readonly number[]): string[] {
  if (decadeStarts.length === 0) return []
  const starts = new Set(decadeStarts)
  const out: string[] = []
  for (const m of text.matchAll(AGE_RANGE[locale])) {
    const from = Number(m[1])
    const to = Number(m[2])
    if (to <= from || to - from > 60) continue
    if (starts.has(from) && (starts.has(to - 9) || starts.has(to - 10))) continue
    out.push(`${from}-${to}`)
  }
  return out
}
